<template>
  <v-card class="pa-5">
    <v-form ref="adjustmentForm" v-model="valid">
      <v-row>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Product</p>
          </v-col>
          <v-autocomplete
            v-model="form.product_id"
            :items="products"
            item-text="product_code"
            item-value="id"
            placeholder="Select Product"
            outlined
            dense
            clearable
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Batch</p>
          </v-col>
          <v-autocomplete
            v-model="form.batch_id"
            :items="batches"
            item-text="batch_code"
            item-value="id"
            placeholder="Select Batch"
            outlined
            dense
            clearable
            :rules="[rules.required]"
            :disabled="!form.product_id"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Branch</p>
          </v-col>
          <v-autocomplete
            v-model="form.branch_id"
            :items="branches"
            item-text="branch_code"
            item-value="id"
            placeholder="Select Branch"
            outlined
            dense
            clearable
            :rules="[rules.required]"
            :disabled="!form.product_id"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Store</p>
          </v-col>
          <v-autocomplete
            v-model="form.store_id"
            :items="this.stores"
            item-text="store_code"
            item-value="id"
            placeholder="Select Store"
            outlined
            dense
            clearable
            :rules="[rules.required]"
            :disabled="!form.branch_id"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Quantity to Adjust</p>
          </v-col>
          <v-text-field
            v-model.number="form.quantity"
            placeholder="Enter Quantity to Adjust"
            type="number"
            outlined
            dense
            :rules="[rules.required, rules.positive]"
            :disabled="!form.store_id"
          />
        </v-col>

        <v-col cols="12">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Reason for Adjustment</p>
          </v-col>
          <v-textarea
            v-model="form.reason"
            placeholder="Enter Reason for Adjustment"
            outlined
            dense
            required
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="12">
          <v-alert v-if="error" type="error" outlined>{{ error }}</v-alert>
          <v-btn
            v-if="hasPermission('stock.deduct')"
            color="primary"
            :disabled="loading"
            @click="submit"
            block
            large
          >
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="24"
              color="white"
            ></v-progress-circular>
            <span v-else>Submit Adjustment</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-snackbar v-model="successSnackbar" color="success">
      Adjustment submitted successfully!
      <template v-slot:action="{}">
        <v-btn color="white" text @click="successSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import { StockAdjustmentService } from "@/services/stockAdjustment-service.js";
import { RequisitionService } from "@/services/requisition-service.js";
import { StockReportService } from "@/services/stockReport-service.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "StockAdjustmentForm",
  data() {
    return {
      valid: false,
      loading: false,
      error: "",
      successSnackbar: false,
      products: [],
      batches: [],
      stores: [],
      branches: [],
      availableStock: 0,
      form: {
        product_id: null,
        product_code: null,
        batch_id: null,
        branch_id: null,
        store_id: null,
        quantity: null,
        adjustment_type: "Deduct",
        reason: "",
      },
      rules: {
        required: (v) => !!v || "This field is required.",
        positive: (v) =>
          v <= this.availableStock ||
          "Can't exceed stock in hand quantity.(" + this.availableStock + ")",
      },
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  watch: {
    "form.product_id"() {
      this.form.batch_id = null;
      this.form.branch_id = null;
      this.form.store_id = null;
      this.fetchStocksForProduct();
    },
    "form.batch_id"() {
      this.form.branch_id = null;
      this.form.store_id = null;
      this.fetchBranchesForBatch();
    },
    "form.branch_id"() {
      this.form.store_id = null;
      this.fetchStoresForBranch();
    },
    "form.store_id"() {
      this.fetchAdjustStock();
    },
  },
  created() {
    this.fetchProducts();
  },
  methods: {
    async fetchProducts(searchTerm) {
      try {
        const res = await RequisitionService.getProducts(searchTerm);
        this.products = res.data.data;
      } catch (err) {
        this.error = "Failed to load products.";
      }
    },
    async fetchBatches() {
      this.form.batch_id = null;
      this.batches = [];
      this.availableStock = 0;
      if (!this.form.product_id) return;
      try {
        const res = await StockAdjustmentService.getBatches(
          this.form.product_id
        );
        this.batches = res.data;
      } catch (err) {
        this.error = "Failed to load batches.";
      }
    },
    async fetchBranches() {
      this.form.branch_id = null;
      this.branches = [];
      this.availableStock = 0;
      if (!this.form.product_id || !this.form.batch_id) return;
      try {
        const res = await StockReportService.getStockReport({
          product: this.form.product_id,
          batch: this.form.batch_id,
        });
        const stockArray = res.data.data || [];
        const branchMap = {};
        stockArray.forEach((item) => {
          if (!branchMap[item.branch_id]) {
            branchMap[item.branch_id] = item.branch_code;
          }
        });
        this.branches = Object.entries(branchMap).map(([id, code]) => ({
          id: Number(id),
          branch_code: code,
        }));
      } catch (err) {
        this.error = "Failed to load branches.";
      }
    },

    async fetchStocksForProduct() {
      this.stocks = [];
      this.batches = [];
      this.branches = [];
      if (!this.form.product_id) return;
      try {
        const res = await StockReportService.getStockReport({
          product: this.form.product_id,
        });
        this.stocks = res.data.data || [];
        const batchMap = {};
        this.stocks.forEach((item) => {
          if (!batchMap[item.batch_id]) {
            batchMap[item.batch_id] = item.batch_code;
          }
        });
        this.batches = Object.entries(batchMap).map(([id, code]) => ({
          id: Number(id),
          batch_code: code,
        }));
      } catch (err) {
        this.error = "Failed to fetch stocks.";
      }
    },
    async fetchBranchesForBatch() {
      this.branches = [];
      this.stores = [];
      if (!this.form.product_id || !this.form.batch_id) return;

      const res = await StockReportService.getStockReport({
        product: this.form.product_id,
        batch: this.form.batch_id,
      });
      this.stocks = res.data.data || [];
      const filteredStocks = this.stocks.filter(
        (item) => item.batch_id === this.form.batch_id
      );
      const branchMap = {};
      filteredStocks.forEach((item) => {
        if (!branchMap[item.branch_id]) {
          branchMap[item.branch_id] = item.branch_code;
        }
      });
      this.branches = Object.entries(branchMap).map(([id, code]) => ({
        id: Number(id),
        branch_code: code,
      }));
    },
    async fetchStoresForBranch() {
      this.form.store_id = null;
      if (
        !this.form.product_id ||
        !this.form.batch_id ||
        !this.form.branch_id
      ) {
        return;
      }
      try {
        const res = await StockReportService.getStockReport({
          product: this.form.product_id,
          batch: this.form.batch_id,
          branch: this.form.branch_id,
        });
        this.stocks = res.data.data || [];
        const storeMap = {};
        this.stocks.forEach((item) => {
          if (!storeMap[item.store_id]) {
            storeMap[item.store_id] = item.store_code;
          }
        });
        this.stores = Object.entries(storeMap).map(([id, code]) => ({
          id: Number(id),
          store_code: code,
        }));
      } catch (err) {
        this.error = "Failed to fetch stores.";
      }
    },
    async fetchAdjustStock() {
      if (
        !this.form.product_id ||
        !this.form.batch_id ||
        !this.form.branch_id ||
        !this.form.store_id
      )
        return;
      try {
        const res = await StockReportService.getStockReport({
          product: this.form.product_id,
          batch: this.form.batch_id,
          branch: this.form.branch_id,
          store: this.form.store_id,
        });
        this.stocks = res.data.data || [];
        if (this.stocks.length > 0) {
          this.availableStock = parseFloat(
            this.stocks[0].stock_in_hand_quantity
          );
          this.form.stock_uuid = this.stocks[0].uuid;
        } else {
          this.availableStock = 0;
        }
      } catch (err) {
        this.error = "Failed to fetch available stock.";
      }
    },
    async submit() {
      this.error = "";
      if (!this.$refs.adjustmentForm.validate()) return;

      if (this.form.adjustment_type === "Add" && this.form.quantity <= 0) {
        this.error = "Quantity must be a positive number.";
        return;
      }
      this.loading = true;
      this.products.forEach((item) => {
        if (this.form.product_id == item.id) {
          this.form.product_code = item.product_code;
        }
      });
      try {
        await StockAdjustmentService.createAdjustment({
          ...this.form,
          user_id: localStorage.getItem("userId"),
        });
        this.successSnackbar = true;
        this.form = {
          product_id: null,
          batch_id: null,
          quantity: null,
          adjustment_type: null,
          reason: "",
        };
        this.availableStock = 0;
        this.$router.push({ name: "StockReport" });
      } catch (err) {
        this.error =
          err.response?.data?.message || "Failed to submit adjustment.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
